<template>
  <ContentWrap>
    <el-form ref="queryFormRef" :inline="true" :model="queryParams">
      <el-form-item label="直播间 ID" prop="liveId">
        <LiveRoomIdSelect v-model="queryParams.liveId" select-class="!w-260px" />
      </el-form-item>
      <el-form-item label="处理状态" prop="status">
        <el-select v-model="queryParams.status" clearable placeholder="全部状态" class="!w-160px">
          <el-option
            v-for="item in statusOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="录制时间" prop="startTime">
        <el-date-picker
          v-model="queryParams.startTime"
          type="datetimerange"
          value-format="YYYY-MM-DD HH:mm:ss"
          start-placeholder="开始时间"
          end-placeholder="结束时间"
          class="!w-360px"
        />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="handleQuery"><Icon icon="ep:search" />查询</el-button>
        <el-button @click="handleResetQuery"><Icon icon="ep:refresh" />重置</el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-table v-loading="loading" :data="recordingList" border>
      <el-table-column label="录制 ID" prop="id" width="100" />
      <el-table-column label="直播间 ID" prop="liveId" width="110" />
      <el-table-column label="云文件信息" min-width="220">
        <template #default="{ row }">
          <div class="file-info">
            <span>{{ row.fileId || '暂无文件 ID' }}</span>
            <small>{{ row.providerRecordId || '暂无录制任务 ID' }}</small>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="格式/清晰度" width="130">
        <template #default="{ row }">{{
          [row.format, row.definition].filter(Boolean).join(' / ') || '-'
        }}</template>
      </el-table-column>
      <el-table-column label="时长" width="100">
        <template #default="{ row }">{{ formatDuration(row.duration) }}</template>
      </el-table-column>
      <el-table-column label="大小" width="110">
        <template #default="{ row }">{{ formatFileSize(row.fileSize) }}</template>
      </el-table-column>
      <el-table-column label="状态" width="110">
        <template #default="{ row }">
          <el-tag :type="getStatusType(row.status)">{{ getStatusLabel(row.status) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="主回放" width="100">
        <template #default="{ row }">
          <el-tag v-if="row.primary" type="success" effect="dark">是</el-tag>
          <span v-else>-</span>
        </template>
      </el-table-column>
      <el-table-column label="录制开始" prop="startTime" :formatter="dateFormatter" width="180" />
      <el-table-column label="录制结束" prop="endTime" :formatter="dateFormatter" width="180" />
      <el-table-column label="操作" width="235" fixed="right">
        <template #default="{ row }">
          <el-button link type="primary" :disabled="!row.fileUrl" @click="handlePreview(row)"
            >播放预览</el-button
          >
          <el-button
            v-if="row.status === RECORDING_STATUS_AVAILABLE && !row.primary"
            v-hasPermi="['live:recording:set-primary']"
            link
            type="success"
            @click="handleSetPrimary(row)"
            >设为主回放</el-button
          >
          <el-button
            v-if="row.status !== RECORDING_STATUS_DISABLED"
            v-hasPermi="['live:recording:disable']"
            link
            type="danger"
            @click="handleDisable(row)"
            >禁用</el-button
          >
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:page="queryParams.pageNo"
      v-model:limit="queryParams.pageSize"
      :total="total"
      @pagination="handleGetList"
    />
  </ContentWrap>

  <Dialog v-model="previewVisible" title="回放预览" width="760px" @closed="handleClosePreview">
    <!-- 只在对话框打开时挂载视频，关闭后停止网络和解码资源 -->
    <video
      v-if="previewVisible && previewUrl"
      :src="previewUrl"
      controls
      autoplay
      class="preview-video"
    ></video>
    <el-empty v-else description="当前文件没有可播放地址" />
  </Dialog>
</template>

<script setup lang="ts">
/**
 * 功能说明：管理直播录制文件，支持查询、预览、设为主回放和禁用。
 */
// ==================== 依赖引入 ====================
import { ContentWrap } from '@/components/ContentWrap'
import { Dialog } from '@/components/Dialog'
import * as RecordingApi from '@/api/live/recording'
import type { LiveRecording, LiveRecordingPageParams } from '@/api/live/recording'
import { dateFormatter } from '@/utils/formatTime'
import LiveRoomIdSelect from '@/views/live/components/LiveRoomIdSelect.vue'

// ==================== 类型定义 ====================
type TagType = 'success' | 'warning' | 'info' | 'danger'

// ==================== Props & Emits ====================
// 页面组件无外部 Props 和 Emits。

// ==================== 响应式数据 ====================
const message = useMessage()
const RECORDING_STATUS_AVAILABLE = 1
const RECORDING_STATUS_DISABLED = 2
const loading = ref(false)
const operating = ref(false)
const recordingList = ref<LiveRecording[]>([])
const total = ref(0)
const queryFormRef = ref()
const previewVisible = ref(false)
const previewUrl = ref('')
const queryParams = reactive<LiveRecordingPageParams>({ pageNo: 1, pageSize: 10 })
const statusOptions = [
  { label: '处理中', value: 0, type: 'warning' },
  { label: '可用', value: RECORDING_STATUS_AVAILABLE, type: 'success' },
  { label: '已禁用', value: RECORDING_STATUS_DISABLED, type: 'info' }
] as const

// ==================== 计算属性 ====================
// 当前页面无需额外计算属性。

// ==================== 生命周期 ====================
onMounted(() => handleGetList())

// ==================== 方法 ====================
/** 加载录制文件列表 */
const handleGetList = async () => {
  loading.value = true
  try {
    const data = await RecordingApi.getRecordingPage(queryParams)
    recordingList.value = data.list || []
    total.value = data.total || 0
  } catch (error) {
    message.error('录制文件加载失败')
  } finally {
    loading.value = false
  }
}

/** 按当前条件查询 */
const handleQuery = () => {
  queryParams.pageNo = 1
  handleGetList()
}

/** 重置查询条件 */
const handleResetQuery = () => {
  queryFormRef.value?.resetFields()
  handleQuery()
}

/** 打开视频预览 */
const handlePreview = (recording: LiveRecording) => {
  previewUrl.value = recording.fileUrl || ''
  previewVisible.value = true
}

/** 关闭预览后清理视频地址 */
const handleClosePreview = () => {
  previewUrl.value = ''
}

/** 将录制文件设为主回放 */
const handleSetPrimary = async (recording: LiveRecording) => {
  if (operating.value) return
  await message.confirm('确认将该文件设为当前直播间的主回放吗？')
  operating.value = true
  try {
    await RecordingApi.setPrimaryRecording(recording.id)
    message.success('主回放设置成功')
    await handleGetList()
  } catch (error: any) {
    message.error(error?.message || '主回放设置失败，请确认录制文件已处理完成')
  } finally {
    operating.value = false
  }
}

/** 禁用录制文件 */
const handleDisable = async (recording: LiveRecording) => {
  if (operating.value) return
  await message.confirm('禁用后该文件将无法对观众播放，是否继续？')
  operating.value = true
  try {
    await RecordingApi.disableRecording(recording.id)
    message.success('录制文件已禁用')
    await handleGetList()
  } catch (error) {
    message.error('录制文件禁用失败')
  } finally {
    operating.value = false
  }
}

/** 格式化录制时长 */
const formatDuration = (seconds?: number) => {
  if (seconds === undefined || seconds === null) return '-'
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  return `${minutes}:${String(remainingSeconds).padStart(2, '0')}`
}

/** 格式化录制文件大小 */
const formatFileSize = (bytes?: number) => {
  if (bytes === undefined || bytes === null) return '-'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

/** 获取录制状态文案 */
const getStatusLabel = (status: number) =>
  statusOptions.find((item) => item.value === status)?.label || '未知'

/** 获取录制状态标签类型 */
const getStatusType = (status: number): TagType =>
  (statusOptions.find((item) => item.value === status)?.type || 'info') as TagType
</script>

<style scoped>
.file-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.file-info small {
  color: var(--el-text-color-secondary);
}

.preview-video {
  width: 100%;
  max-height: 480px;
  background: #000;
  border-radius: 8px;
}
</style>
